import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import dummyUser from "../../../assets/images/dummy/dummy-user.png";
import styles from "./massengermodal.module.css";
import "../../../pages/UserProfile/Components/LinkedInChatDock/LinkedInChatDock.css";
import useMessenger from "../../../hooks/useMessenger";
import useMessengerProfileTabs from "../../../hooks/useMessengerProfileTabs";
import { useLazyGetUserInfoQuery } from "../../../features/user/userApiSlice";
import {
  getMessengerProfileTypeKey,
  getMessengerProfileTypeLabel,
} from "../../../utils/messengerProfiles";
import RightSectionModule, {
  RightSection as RightSectionNamed,
} from "./RightSection";
import LeftSectionModule, {
  LeftSection as LeftSectionNamed,
} from "./LeftSection";
import CenterSectionModule, {
  CenterSection as CenterSectionNamed,
} from "./CenterSection";
import MessengerProfileTabsModule, {
  MessengerProfileTabs as MessengerProfileTabsNamed,
} from "./MessengerProfileTabs";

const resolveComponent = (mod, named) => {
  if (typeof mod === "function") return mod;
  if (mod && typeof mod.default === "function") return mod.default;
  if (typeof named === "function") return named;
  if (named && typeof named.default === "function") return named.default;
  return () => null;
};

const CloseComp = resolveComponent(CloseIcon, () => null);
const ChevronDownComp = resolveComponent(KeyboardArrowDownIcon, () => null);
const ChevronUpComp = resolveComponent(KeyboardArrowUpIcon, () => null);
const LeftSection = resolveComponent(LeftSectionModule, LeftSectionNamed);
const CenterSection = resolveComponent(CenterSectionModule, CenterSectionNamed);
const RightSection = resolveComponent(RightSectionModule, RightSectionNamed);
const MessengerProfileTabs = resolveComponent(
  MessengerProfileTabsModule,
  MessengerProfileTabsNamed,
);

const getProfileText = (value) => {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return "";
  if (typeof value.text === "string" && value.text.trim())
    return value.text.trim();
  if (typeof value.name === "string" && value.name.trim())
    return value.name.trim();

  return [
    value.street,
    value.addressLine,
    value.city,
    value.district,
    value.state,
    value.province,
    value.region,
    value.country,
    value.postalCode,
    value.zipCode,
  ]
    .filter((part) => typeof part === "string" && part.trim())
    .map((part) => part.trim())
    .filter((part, index, parts) => parts.indexOf(part) === index)
    .join(", ");
};

const MassengerModals = ({
  setLgMsgShow,
  lgMsgShow,
  shopid,
  initialUser,
  initialProfile,
  focusProfile = false,
  onInitialUserConsumed,
  pageMode = false,
  isDockMinimized = false,
  isDockMaximized = false,
}) => {
  const [selectedConnectedTabId, setSelectedConnectedTabId] = useState("");
  const [selectedConnectedTabOwnerKey, setSelectedConnectedTabOwnerKey] =
    useState("");
  const [isChatDockMinimized, setIsChatDockMinimized] = useState(false);
  const [isActiveChatMaximized, setIsActiveChatMaximized] = useState(false);

  const handleToggleActiveChatMinimize = (e) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    if (isActiveChatMaximized) {
      setIsActiveChatMaximized(false);
    }
    setIsChatDockMinimized((prev) => !prev);
  };
  const {
    selectedUser,
    setSelectedUser,
    restoredChatId,
    restoredChatProfile,
    messagesLoading,
    setRestoredChatId,
    setRestoredChatProfile,
    chatHistory,
    anchorEl,
    setAnchorEl,
    activeMessage,
    setActiveMessage,
    name,
    currentProfileId,
    isContactBlocked,
    isContactBlockStatusLoaded,
    users,
    chatsLoading,
    chatsError,
    refetchChats,
    connectionsLoading,
    searchedData,
    connectedShops,
    switchedProfile,
    searchTerm,
    isFullscreen,
    showEmojiPicker,
    setShowEmojiPicker,
    messageInputRef,
    emojiPickerRef,
    toggleButtonRef,
    searchInputRef,
    messageAreaRef,
    modalRef,
    safeRender,
    getAvatarUrl,
    safeGetAsset,
    toggleFullscreen,
    ProfileSelected,
    handleSearch,
    handleSearchDataClick,
    handleFileUpload,
    selectedFile,
    filePreview,
    isUploadingMedia,
    handleRemoveSelectedFile,
    handleSend,
    onEmojiClick,
    handleClose,
    handleUserClick,
    formatTime,
    sendMessage,
    typingStatus,
    onlineStatus,
    replyingTo,
    setReplyingTo,
    handleDeleteMessage,
    handleClearChat,
    handleToggleContactBlock,
    handleCloseMenu,
    handleCloseActiveChat,
  } = useMessenger(
    setLgMsgShow,
    shopid,
    pageMode ? !isDockMinimized && Boolean(lgMsgShow) : lgMsgShow,
  );

  const {
    activeChatsUnreadCount,
    activeProfileShops,
    activeTabLabel,
    activeTabType,
    isMarketerActive,
    isUserActive,
    shopFocusMode,
    focusedShopProfile,
    profiles,
    profileTabs,
    selectedProfile,
    handleSelect,
    isLoading: profilesLoading,
    profilesError,
    refetchProfiles,
    shopTabConnections,
    shopTabConnectionsLoading,
    shopTabConnectionsError,
    refetchShopTabConnections,
    shopTabConnectionSourceCount,
  } = useMessengerProfileTabs({
    ProfileSelected,
    initialProfile,
    focusProfile,
    pageMode,
    name,
    users,
    searchTerm,
  });
  const getConnectedTabId = (profile) => {
    const position = String(profile?.position || "").toLowerCase();
    const businessType = String(
      profile?.businessType ||
        profile?.linkBusinessType ||
        profile?.otherbusinessType ||
        "",
    )
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    if (
      position === "user" ||
      businessType === "generalpublic" ||
      businessType === "user"
    ) {
      return "type:user";
    }

    const profileType = getMessengerProfileTypeKey({
      ...profile,
      businessType:
        profile?.businessType ||
        profile?.linkBusinessType ||
        profile?.otherbusinessType,
    });
    const normalizedType = String(profileType).toLowerCase();
    return `type:${
      normalizedType === "generalpublic" || normalizedType === "user"
        ? "user"
        : profileType
    }`;
  };

  const selectedProfileId = String(
    selectedProfile?.id ||
      selectedProfile?._id ||
      selectedProfile?.shopid ||
      (selectedProfile?.isGeneralPublic ? "user" : "") ||
      "",
  );

  const connectedProfileOwnerKey = isUserActive
    ? `user:${name?._id || ""}`
    : `shop:${String(
        selectedProfile?.shopid ||
          selectedProfile?.shopId ||
          selectedProfile?.id ||
          selectedProfile?._id ||
          "",
      )}`;

  useEffect(() => {
    if (!selectedProfile || selectedProfile.isGeneralPublic) {
      setSelectedConnectedTabId("type:user");
      setSelectedConnectedTabOwnerKey("");
    } else {
      setSelectedConnectedTabId(getConnectedTabId(selectedProfile));
      setSelectedConnectedTabOwnerKey(connectedProfileOwnerKey);
    }
  }, [selectedProfileId, isUserActive, connectedProfileOwnerKey]);

  useEffect(() => {
    if (selectedUser?.otherParticipantData) {
      setIsChatDockMinimized(false);
    }
  }, [
    selectedUser?.otherParticipantData?._id,
    selectedUser?.otherParticipantData?.userid,
    selectedUser?.otherParticipantData?.shopid,
  ]);

  useEffect(() => {
    if (initialProfile?.type !== "user" || !isUserActive || !focusProfile)
      return;

    setSelectedConnectedTabId("type:user");
    setSelectedConnectedTabOwnerKey(connectedProfileOwnerKey);
    setRestoredChatId("");
    setRestoredChatProfile(null);
  }, [
    initialProfile?.type,
    initialProfile?.profileId,
    isUserActive,
    focusProfile,
    connectedProfileOwnerKey,
    setRestoredChatId,
    setRestoredChatProfile,
  ]);
  const activeProfileLabel = isMarketerActive
    ? "Marketing Profile"
    : isUserActive
      ? "General Public"
      : activeTabLabel;
  const ownEntityIds = new Set(
    [
      name?._id,
      name?.id,
      ...(activeProfileShops || []).flatMap((s) => [
        s.shopid,
        s.shopId,
        s.id,
        s._id,
        s.serviceid,
        s.companyid,
      ]),
      ...(profiles || []).flatMap((s) => [
        s.shopid,
        s.shopId,
        s.id,
        s._id,
        s.serviceid,
        s.companyid,
      ]),
    ]
      .filter(Boolean)
      .map(String),
  );

  const isOwnEntity = (profile) =>
    [
      profile?.shopid,
      profile?.shopId,
      profile?.serviceid,
      profile?.companyid,
      profile?.userid,
      profile?.userId,
      profile?.id,
      profile?._id,
    ]
      .filter(Boolean)
      .some((id) => ownEntityIds.has(String(id)));

  const activeConnectedProfiles = shopTabConnections.filter(
    (profile) => !isOwnEntity(profile),
  );
  const connectedProfileTabs = activeConnectedProfiles.reduce(
    (tabs, profile) => {
      const tabId = getConnectedTabId(profile);
      const existingTab = tabs.find((tab) => tab.id === tabId);
      if (existingTab) {
        existingTab.connectedProfiles.push(profile);
        existingTab.unreadCount += Number(profile.unreadCount) || 0;
        return tabs;
      }

      const profileType = String(
        getMessengerProfileTypeKey(profile),
      ).toLowerCase();
      const isGeneralPublic =
        getConnectedTabId(profile) === "type:user" ||
        profileType === "generalpublic";
      tabs.push({
        ...profile,
        id: tabId,
        _id: tabId,
        connectedProfileTabId: tabId,
        connectedProfiles: [profile],
        tabLabel: isGeneralPublic
          ? "General Public"
          : getMessengerProfileTypeLabel(profile),
        unreadCount: Number(profile.unreadCount) || 0,
      });
      return tabs;
    },
    [],
  );
  const activeChatParticipant =
    selectedUser?.otherParticipantData ||
    (selectedUser?._id || selectedUser?.userid || selectedUser?.shopid || selectedUser?.id
      ? {
          _id:
            selectedUser.userid ||
            selectedUser.shopid ||
            selectedUser.id ||
            selectedUser._id,
          fullname:
            selectedUser.fullname ||
            selectedUser.shopname ||
            selectedUser.name ||
            "Chat",
          shopname:
            selectedUser.shopname ||
            selectedUser.fullname ||
            selectedUser.name ||
            "Shop",
          image:
            selectedUser.profilePic ||
            selectedUser.image ||
            selectedUser.imageUrl ||
            null,
          position: selectedUser.position || "User",
          businessType: selectedUser.businessType || "user",
        }
      : null);

  const normalizedSelectedUser = selectedUser
    ? selectedUser.otherParticipantData
      ? selectedUser
      : { ...selectedUser, otherParticipantData: activeChatParticipant }
    : null;

  useEffect(() => {
    if (selectedUser) {
      setIsChatDockMinimized(false);
    }
  }, [
    selectedUser?._id,
    selectedUser?.otherParticipantData?._id,
    selectedUser?.otherParticipantData?.shopid,
    selectedUser?.otherParticipantData?.userid,
  ]);

  const openChatProfile = activeChatParticipant;
  const restoredProfileTarget = restoredChatProfile || openChatProfile;
  const openChatBusinessType = String(
    restoredProfileTarget?.businessType ||
      restoredProfileTarget?.type ||
      selectedUser?.otherbusinessType ||
      "",
  )
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  const isOpenChatUser = Boolean(
    restoredProfileTarget?.position === "User" ||
    openChatBusinessType === "generalpublic" ||
    openChatBusinessType === "user",
  );
  const openChatEntityId = String(
    isOpenChatUser
      ? restoredProfileTarget?.userid ||
          restoredProfileTarget?.userId ||
          restoredProfileTarget?._id ||
          restoredProfileTarget?.id ||
          ""
      : restoredProfileTarget?.shopid ||
          restoredProfileTarget?.shopId ||
          restoredProfileTarget?.shopContextId ||
          restoredProfileTarget?.companyid ||
          restoredProfileTarget?.serviceid ||
          restoredProfileTarget?._id ||
          restoredProfileTarget?.id ||
          "",
  );
  const openChatEntityName = String(
    isOpenChatUser
      ? restoredProfileTarget?.fullname ||
          restoredProfileTarget?.name ||
          restoredProfileTarget?.shopname ||
          ""
      : restoredProfileTarget?.shopname ||
          restoredProfileTarget?.shopName ||
          restoredProfileTarget?.companyname ||
          restoredProfileTarget?.companyName ||
          restoredProfileTarget?.name ||
          restoredProfileTarget?.fullname ||
          "",
  )
    .trim()
    .toLowerCase();
  const restoredConnectedProfileById =
    restoredChatId && openChatEntityId
      ? activeConnectedProfiles.find((profile) => {
          const profileEntityId = String(
            isOpenChatUser
              ? profile.userid ||
                  profile.userId ||
                  profile.id ||
                  profile._id ||
                  ""
              : profile.shopid ||
                  profile.shopId ||
                  profile.shopContextId ||
                  profile.id ||
                  profile._id ||
                  "",
          );
          return profileEntityId && profileEntityId === openChatEntityId;
        })
      : null;
  const restoredConnectedProfile =
    restoredConnectedProfileById ||
    (restoredChatId && openChatEntityName
      ? activeConnectedProfiles.find((profile) => {
          const profileName = String(
            isOpenChatUser
              ? profile.fullname ||
                  profile.name ||
                  profile.shopname ||
                  profile.displayName ||
                  ""
              : profile.shopname ||
                  profile.shopName ||
                  profile.companyname ||
                  profile.companyName ||
                  profile.name ||
                  profile.displayName ||
                  "",
          )
            .trim()
            .toLowerCase();
          return profileName === openChatEntityName;
        })
      : null);
  const restoredConnectedTabId = restoredConnectedProfile
    ? getConnectedTabId(restoredConnectedProfile)
    : restoredChatId && restoredProfileTarget
      ? getConnectedTabId(restoredProfileTarget)
      : "";
  const connectedProfileLoading = isUserActive
    ? connectionsLoading
    : shopTabConnectionsLoading;
  const effectiveSelectedConnectedTabId = selectedConnectedTabId
    ? selectedConnectedTabId
    : restoredChatId
      ? restoredConnectedTabId
      : isUserActive
        ? "type:user"
        : activeTabType
          ? `type:${activeTabType}`
          : "type:user";

  const normEffectiveTab = String(effectiveSelectedConnectedTabId || "")
    .replace(/^type:/, "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  const isMatchingCategory = (a, b) => {
    if (!a || !b) return false;
    const normA = String(a)
      .replace(/^type:/, "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    const normB = String(b)
      .replace(/^type:/, "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    if (normA === normB) return true;
    if (normA.includes("whole") && normB.includes("whole")) return true;
    if (normA.includes("retail") && normB.includes("retail")) return true;
    if (
      (normA.includes("food") || normA.includes("restaurant")) &&
      (normB.includes("food") || normB.includes("restaurant"))
    )
      return true;
    if (normA.includes("service") && normB.includes("service")) return true;
    if (normA.includes("manufacturer") && normB.includes("manufacturer"))
      return true;
    if (normA.includes("rawmaterial") && normB.includes("rawmaterial"))
      return true;
    if (normA.includes("gem") && normB.includes("gem")) return true;
    if (
      (normA.includes("vehicle") || normA.includes("usedvehicle")) &&
      (normB.includes("vehicle") || normB.includes("usedvehicle"))
    )
      return true;
    if (normA.includes("used") && normB.includes("used")) return true;
    if (normA.includes("farm") && normB.includes("farm")) return true;
    if (normA.includes("recycle") && normB.includes("recycle")) return true;
    if (normA.includes("transport") && normB.includes("transport")) return true;
    if (normA.includes("property") && normB.includes("property")) return true;
    if (normA.includes("job") && normB.includes("job")) return true;
    if (normA.includes("market") && normB.includes("market")) return true;
    return false;
  };

  const normActiveTabType = String(activeTabType || "")
    .replace(/^type:/, "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  const isUserIdentityTab = Boolean(
    isUserActive &&
    (!normEffectiveTab ||
      normEffectiveTab === "user" ||
      normEffectiveTab === "generalpublic")
  );

  const isShopProfileTab = Boolean(
    !isUserActive &&
    !isMarketerActive &&
    normActiveTabType &&
    (normEffectiveTab === normActiveTabType ||
      isMatchingCategory(normEffectiveTab, normActiveTabType))
  );

  const isMarketerProfileTab = Boolean(
    isMarketerActive &&
    (normEffectiveTab === "marketershop" ||
      normEffectiveTab === normActiveTabType)
  );

  const isCategoryFilterActive = Boolean(
    effectiveSelectedConnectedTabId &&
    !isUserIdentityTab &&
    !isShopProfileTab &&
    !isMarketerProfileTab
  );

  const selectedConnectedTab = connectedProfileTabs.find((tab) => {
    const tId = String(tab.connectedProfileTabId || tab.id || "")
      .replace(/^type:/, "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    return (
      tId &&
      (tId === normEffectiveTab || isMatchingCategory(tId, normEffectiveTab))
    );
  });

  const visibleConnectedProfiles = normEffectiveTab
    ? selectedConnectedTab?.connectedProfiles?.length
      ? selectedConnectedTab.connectedProfiles
      : activeConnectedProfiles.filter((profile) => {
          const pTabId = String(getConnectedTabId(profile) || "")
            .replace(/^type:/, "")
            .toLowerCase()
            .replace(/[\s_-]+/g, "");
          return (
            pTabId === normEffectiveTab ||
            isMatchingCategory(pTabId, normEffectiveTab)
          );
        })
    : activeConnectedProfiles;
  const focusedProfileContext = pageMode && shopFocusMode;
  const activeProfileName = focusedProfileContext
    ? isUserActive
      ? "General Public"
      : focusedShopProfile?.displayName ||
        focusedShopProfile?.shopname ||
        focusedShopProfile?.name ||
        activeProfileLabel
    : openChatProfile
      ? openChatProfile.fullname ||
        openChatProfile.shopname ||
        openChatProfile.name ||
        activeProfileLabel
      : isUserActive
        ? "General Public"
        : selectedProfile?.displayName ||
          selectedProfile?.name ||
          activeProfileLabel;
  const activeProfileLocation = focusedProfileContext
    ? getProfileText(selectedProfile?.location) ||
      getProfileText(selectedProfile?.shopAddress) ||
      getProfileText(selectedProfile?.shopaddress) ||
      getProfileText(selectedProfile?.address)
    : openChatProfile
      ? getProfileText(openChatProfile.publicAddress) ||
        getProfileText(openChatProfile.address) ||
        getProfileText(openChatProfile.location) ||
        getProfileText(openChatProfile.shopaddress)
      : getProfileText(selectedProfile?.location) ||
        getProfileText(selectedProfile?.shopAddress) ||
        getProfileText(selectedProfile?.shopaddress) ||
        getProfileText(selectedProfile?.address);
  const locationUserId = isOpenChatUser
    ? openChatProfile?._id || openChatProfile?.userid
    : isUserActive
      ? name?._id
      : null;
  const [getUserInfo] = useLazyGetUserInfoQuery();
  const [databaseUserLocation, setDatabaseUserLocation] = useState("");

  useEffect(() => {
    setDatabaseUserLocation("");
    if (!locationUserId) return undefined;

    let active = true;
    getUserInfo(locationUserId)
      .unwrap()
      .then((response) => {
        const user =
          response?.data?.User || response?.User || response?.data || response;
        const location =
          getProfileText(user?.address) ||
          getProfileText(user?.location) ||
          [user?.MAddress, user?.MCity, user?.MProvince, user?.MCountry]
            .filter((part) => typeof part === "string" && part.trim())
            .join(", ");
        if (active) setDatabaseUserLocation(location);
      })
      .catch((error) => {
        console.warn("Unable to load profile location.", error);
      });

    return () => {
      active = false;
    };
  }, [getUserInfo, locationUserId]);
  const profileTabsElement = (
    <MessengerProfileTabs
      activeChatsUnreadCount={activeChatsUnreadCount}
      handleSelect={handleSelect}
      connectedProfiles={connectedProfileTabs}
      connectedProfilesLoading={connectedProfileLoading}
      connectedProfilesError={isUserActive ? "" : shopTabConnectionsError}
      selectedConnectedTabId={effectiveSelectedConnectedTabId}
      onSelectUserProfile={(userProfile) => {
        setSelectedConnectedTabId("type:user");
        setSelectedConnectedTabOwnerKey("");
        setRestoredChatId("");
        setRestoredChatProfile(null);
        if (userProfile) handleSelect(userProfile);
      }}
      onSelectShopProfile={(profile) => {
        const tabId = getConnectedTabId(profile);
        setSelectedConnectedTabId(tabId);
        setSelectedConnectedTabOwnerKey("");
        setRestoredChatId("");
        setRestoredChatProfile(null);
        if (profile) handleSelect(profile);
      }}
      onSelectMarketerProfile={(profile) => {
        setSelectedConnectedTabId("type:marketerShop");
        setSelectedConnectedTabOwnerKey("");
        setRestoredChatId("");
        setRestoredChatProfile(null);
        if (profile) handleSelect(profile);
      }}
      onOpenConnectedProfile={(profile) => {
        setRestoredChatId("");
        setRestoredChatProfile(null);
        const targetTabId =
          profile?.connectedProfileTabId ||
          profile?.tabId ||
          (profile ? getConnectedTabId(profile) : "");
        if (targetTabId) {
          setSelectedConnectedTabId(targetTabId);
        }
        setSelectedConnectedTabOwnerKey(connectedProfileOwnerKey);
      }}
      isMarketerActive={isMarketerActive}
      isUserActive={isUserActive}
      forceGeneralPublicActive={
        initialProfile?.type === "user" &&
        isUserActive &&
        !effectiveSelectedConnectedTabId
      }
      isLoading={profilesLoading}
      isError={profilesError}
      onRetry={refetchProfiles}
      pageMode={pageMode}
      focusProfile={focusProfile}
      focusedShopProfile={focusedShopProfile}
      profiles={profiles}
      profileTabs={profileTabs}
      selectedProfile={selectedProfile}
    />
  );

  // When a target user is passed from another page (e.g. Send Message button),
  // auto-create / select that chat and clear the initial user prop.
  useEffect(() => {
    if (!initialUser || !lgMsgShow) return;

    const autoOpen = async () => {
      try {
        // Build a chat entry that mirrors the shape handleSearchDataClick produces
        const targetData = {
          shopname: initialUser.fullname,
          profilePic: initialUser.image || null,
          businessType: "generalPublic",
          userid: initialUser._id,
          position: "User",
          location: "",
          status: "",
        };
        await handleSearchDataClick(targetData);
      } catch (err) {
        console.error("Failed to auto-open chat:", err);
      } finally {
        if (onInitialUserConsumed) onInitialUserConsumed();
      }
    };

    autoOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUser, lgMsgShow]);

  const handleMenuOpen = (event, index) => {
    setAnchorEl(event.currentTarget);
    setActiveMessage(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMessage(null);
  };

  return (
    <>
      {lgMsgShow && (
        <div
          className={`${styles.modalOverlay} ${pageMode ? styles.pageOverlay : ""}`}
          style={{ width: "100%" }}
        >
          <div
            ref={modalRef}
            className={`modal-dialog ${styles.modalDialog} ${
              !pageMode && !selectedUser?.otherParticipantData
                ? styles.modalDialogNoRightSection
                : ""
            } ${pageMode ? styles.pageDialog : ""}`}
            role="document"
          >
            <div className={`modal-content`}>
              <div className={styles.header}>
                <div
                  className={`${styles.headerToolbar} ${pageMode ? styles.pageHeaderToolbar : ""}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {pageMode && (
                    <span
                      className={styles.pageBrand}
                      role="img"
                      aria-label="Selectezi"
                    />
                  )}
                  <h2
                    className={`${styles.headerTitle} ${pageMode ? styles.pageHeaderTitle : ""}`}
                  >
                    Messenger
                  </h2>

                  <div
                    className={`${styles.headerActions} ${pageMode ? styles.pageHeaderActions : ""}`}
                  >
                    <button
                      className={styles.closeButton}
                      onClick={handleClose}
                    >
                      ×
                    </button>
                  </div>
                </div>

                {pageMode && (
                  <div className={styles.pageProfileContext}>
                    <strong>{activeProfileName}</strong>
                    <span>
                      {databaseUserLocation || activeProfileLocation
                        ? `Location: ${databaseUserLocation || activeProfileLocation}`
                        : activeProfileLabel}
                    </span>
                  </div>
                )}

                {profileTabsElement}
              </div>

              <div
                className={`modal-body mt-2 ${styles.modalBody} ${
                  pageMode
                    ? styles.dockModalBody
                    : selectedUser?.otherParticipantData
                    ? ""
                    : styles.modalBodyNoSelection
                }`}
                style={
                  !pageMode && !selectedUser?.otherParticipantData
                    ? {
                        display: "grid",
                        gridTemplateColumns:
                          "minmax(210px, 22%) minmax(0, 1fr)",
                      }
                    : pageMode
                    ? {
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        padding: "6px 10px 8px",
                        overflow: "hidden",
                      }
                    : undefined
                }
              >
                {pageMode ? (
                  <LeftSection
                    searchInputRef={searchInputRef}
                    handleSearch={handleSearch}
                    searchTerm={searchTerm}
                    searchedData={searchedData}
                    connectedShops={visibleConnectedProfiles}
                    connectedProfilesOnly={Boolean(
                      effectiveSelectedConnectedTabId &&
                      effectiveSelectedConnectedTabId !== "type:user" &&
                      effectiveSelectedConnectedTabId !== "type:generalpublic",
                    )}
                    selectedConnectedTabId={effectiveSelectedConnectedTabId}
                    selectedConnectedTabLabel={
                      selectedConnectedTab?.tabLabel ||
                      selectedConnectedTab?.label ||
                      activeTabLabel ||
                      ""
                    }
                    userShops={activeProfileShops}
                    activeTabType={activeTabType}
                    activeTabLabel={activeTabLabel}
                    selectedProfile={selectedProfile}
                    onSelectShop={(shop) => handleSelect(shop)}
                    handleSearchDataClick={handleSearchDataClick}
                    safeGetAsset={safeGetAsset}
                    getAvatarUrl={getAvatarUrl}
                    users={users}
                    chatHistory={chatHistory}
                    isLoading={chatsLoading}
                    chatsError={chatsError}
                    onRetryChats={refetchChats}
                    connectionsLoading={
                      connectionsLoading || shopTabConnectionsLoading
                    }
                    connectionsError={shopTabConnectionsError}
                    connectionsSourceCount={shopTabConnectionSourceCount}
                    onRetryConnections={refetchShopTabConnections}
                    selectedUser={selectedUser}
                    handleUserClick={handleUserClick}
                    safeRender={safeRender}
                  />
                ) : (
                  <>
                    <LeftSection
                      searchInputRef={searchInputRef}
                      handleSearch={handleSearch}
                      searchTerm={searchTerm}
                      searchedData={searchedData}
                      connectedShops={visibleConnectedProfiles}
                      connectedProfilesOnly={Boolean(
                        effectiveSelectedConnectedTabId &&
                        effectiveSelectedConnectedTabId !== "type:user" &&
                        effectiveSelectedConnectedTabId !== "type:generalpublic",
                      )}
                      selectedConnectedTabId={effectiveSelectedConnectedTabId}
                      selectedConnectedTabLabel={
                        selectedConnectedTab?.tabLabel ||
                        selectedConnectedTab?.label ||
                        activeTabLabel ||
                        ""
                      }
                      userShops={activeProfileShops}
                      activeTabType={activeTabType}
                      activeTabLabel={activeTabLabel}
                      selectedProfile={selectedProfile}
                      onSelectShop={(shop) => handleSelect(shop)}
                      handleSearchDataClick={handleSearchDataClick}
                      safeGetAsset={safeGetAsset}
                      getAvatarUrl={getAvatarUrl}
                      users={users}
                      chatHistory={chatHistory}
                      isLoading={chatsLoading}
                      chatsError={chatsError}
                      onRetryChats={refetchChats}
                      connectionsLoading={
                        connectionsLoading || shopTabConnectionsLoading
                      }
                      connectionsError={shopTabConnectionsError}
                      connectionsSourceCount={shopTabConnectionSourceCount}
                      onRetryConnections={refetchShopTabConnections}
                      selectedUser={selectedUser}
                      handleUserClick={handleUserClick}
                      safeRender={safeRender}
                    />

                    <CenterSection
                      selectedUser={selectedUser}
                      isChatsLoading={chatsLoading}
                      isMessagesLoading={messagesLoading}
                      currentProfileId={currentProfileId}
                      isContactBlocked={isContactBlocked}
                      isContactBlockStatusLoaded={isContactBlockStatusLoaded}
                      safeGetAsset={safeGetAsset}
                      getAvatarUrl={getAvatarUrl}
                      safeRender={safeRender}
                      switchedProfile={switchedProfile}
                      messageAreaRef={messageAreaRef}
                      chatHistory={chatHistory}
                      name={name}
                      handleMenuOpen={handleMenuOpen}
                      anchorEl={anchorEl}
                      activeMessage={activeMessage}
                      handleMenuClose={handleMenuClose}
                      formatTime={formatTime}
                      showEmojiPicker={showEmojiPicker}
                      emojiPickerRef={emojiPickerRef}
                      onEmojiClick={onEmojiClick}
                      toggleButtonRef={toggleButtonRef}
                      setShowEmojiPicker={setShowEmojiPicker}
                      replyingTo={replyingTo}
                      setReplyingTo={setReplyingTo}
                      handleDeleteMessage={handleDeleteMessage}
                      onClearChat={handleClearChat}
                      onToggleContactBlock={handleToggleContactBlock}
                      handleCloseMenu={handleCloseMenu}
                      handleFileUpload={handleFileUpload}
                      messageInputRef={messageInputRef}
                      sendMessage={sendMessage}
                      typingStatus={typingStatus}
                      onlineStatus={onlineStatus}
                      selectedFile={selectedFile}
                      filePreview={filePreview}
                      isUploadingMedia={isUploadingMedia}
                      handleRemoveSelectedFile={handleRemoveSelectedFile}
                      handleSend={handleSend}
                    />

                    {selectedUser?.otherParticipantData && (
                      <RightSection
                        selectedUser={selectedUser}
                        safeGetAsset={safeGetAsset}
                        getAvatarUrl={getAvatarUrl}
                        sharedSelectedProfile={selectedProfile}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* When in docked/pageMode and a message is clicked, open active conversation dock on the left side with animation */}
      {pageMode &&
        Boolean(normalizedSelectedUser?.otherParticipantData) &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`linkedin-active-chat-dock ${isChatDockMinimized ? "is-minimized" : ""} ${isActiveChatMaximized ? "is-maximized" : ""} ${isDockMinimized ? "is-dock-minimized" : ""} ${isDockMaximized ? "is-dock-maximized" : ""}`}
            role="region"
            aria-label="Active conversation"
          >
            <div
              className="linkedin-active-chat-header"
              onClick={handleToggleActiveChatMinimize}
              title={isChatDockMinimized ? "Click to expand chat" : "Click to minimize chat"}
            >
              <div className="linkedin-active-chat-header-info">
                <div className="linkedin-active-chat-avatar-wrapper">
                  <img
                    src={safeGetAsset(getAvatarUrl(normalizedSelectedUser?.otherParticipantData)) || dummyUser}
                    alt=""
                    className="linkedin-active-chat-avatar"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = dummyUser;
                    }}
                  />
                  <span className="linkedin-chat-dock-online-dot" />
                </div>
                <div className="linkedin-active-chat-title-info">
                  <span className="linkedin-active-chat-name">
                    {safeRender(
                      normalizedSelectedUser?.otherParticipantData?.fullname ||
                      normalizedSelectedUser?.otherParticipantData?.shopname ||
                      normalizedSelectedUser?.otherParticipantData?.name ||
                      "Chat"
                    )}
                  </span>
                  <span className="linkedin-active-chat-status">
                    {safeRender(
                      normalizedSelectedUser?.otherParticipantData?.businessType ||
                      normalizedSelectedUser?.otherParticipantData?.position ||
                      "Active now"
                    )}
                  </span>
                </div>
              </div>

              <div
                className="linkedin-active-chat-actions"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="linkedin-chat-dock-btn"
                  onClick={handleToggleActiveChatMinimize}
                  aria-label={isChatDockMinimized ? "Expand chat" : "Minimize chat"}
                  title={isChatDockMinimized ? "Expand" : "Minimize"}
                >
                  {isChatDockMinimized ? (
                    KeyboardArrowUpIcon ? (
                      <KeyboardArrowUpIcon className="linkedin-chat-dock-icon" />
                    ) : (
                      <span className="linkedin-chat-dock-icon">▲</span>
                    )
                  ) : KeyboardArrowDownIcon ? (
                    <KeyboardArrowDownIcon className="linkedin-chat-dock-icon" />
                  ) : (
                    <span className="linkedin-chat-dock-icon">▼</span>
                  )}
                </button>

                {/* Maximize Button on left side of Close button */}
                <button
                  type="button"
                  className="linkedin-chat-dock-btn maximize-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsActiveChatMaximized((prev) => !prev);
                    if (isChatDockMinimized) {
                      setIsChatDockMinimized(false);
                    }
                  }}
                  aria-label={isActiveChatMaximized ? "Restore chat size" : "Maximize chat"}
                  title={isActiveChatMaximized ? "Restore" : "Maximize"}
                >
                  {isActiveChatMaximized ? (
                    FullscreenExitIcon ? (
                      <FullscreenExitIcon className="linkedin-chat-dock-icon" />
                    ) : (
                      <span className="linkedin-chat-dock-icon">❐</span>
                    )
                  ) : FullscreenIcon ? (
                    <FullscreenIcon className="linkedin-chat-dock-icon" />
                  ) : (
                    <span className="linkedin-chat-dock-icon">□</span>
                  )}
                </button>

                <button
                  type="button"
                  className="linkedin-chat-dock-btn close-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof handleCloseActiveChat === "function") {
                      handleCloseActiveChat();
                    } else {
                      setSelectedUser(null);
                    }
                  }}
                  aria-label="Close chat"
                  title="Close"
                >
                  {CloseIcon ? (
                    <CloseIcon className="linkedin-chat-dock-icon" />
                  ) : (
                    <svg
                      className="linkedin-chat-dock-icon"
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {!isChatDockMinimized && (
              <div className="linkedin-active-chat-body">
                <CenterSection
                  selectedUser={normalizedSelectedUser}
                  isChatsLoading={chatsLoading}
                  isMessagesLoading={messagesLoading}
                  currentProfileId={currentProfileId}
                  isContactBlocked={isContactBlocked}
                  isContactBlockStatusLoaded={isContactBlockStatusLoaded}
                  safeGetAsset={safeGetAsset}
                  getAvatarUrl={getAvatarUrl}
                  safeRender={safeRender}
                  switchedProfile={switchedProfile}
                  messageAreaRef={messageAreaRef}
                  chatHistory={chatHistory}
                  name={name}
                  handleMenuOpen={handleMenuOpen}
                  anchorEl={anchorEl}
                  activeMessage={activeMessage}
                  handleMenuClose={handleMenuClose}
                  formatTime={formatTime}
                  showEmojiPicker={showEmojiPicker}
                  emojiPickerRef={emojiPickerRef}
                  onEmojiClick={onEmojiClick}
                  toggleButtonRef={toggleButtonRef}
                  setShowEmojiPicker={setShowEmojiPicker}
                  replyingTo={replyingTo}
                  setReplyingTo={setReplyingTo}
                  handleDeleteMessage={handleDeleteMessage}
                  onClearChat={handleClearChat}
                  onToggleContactBlock={handleToggleContactBlock}
                  handleCloseMenu={handleCloseMenu}
                  handleFileUpload={handleFileUpload}
                  messageInputRef={messageInputRef}
                  sendMessage={sendMessage}
                  typingStatus={typingStatus}
                  onlineStatus={onlineStatus}
                  selectedFile={selectedFile}
                  filePreview={filePreview}
                  isUploadingMedia={isUploadingMedia}
                  handleRemoveSelectedFile={handleRemoveSelectedFile}
                  handleSend={handleSend}
                />
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export { MassengerModals };
export default MassengerModals;
